import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import selectors from '../../rootSelectors';
import { playAlexAudio, setAlexState } from './AlexActions';
import AlexLottie from './alexLottie';
import ALEX_STATES from './AlexStates';
import { setReadyToAnimate, startSpeaking, stopSpeaking } from '../avatar/avatarActions';
import {
    setPauseButtonActive,
    setPauseButtonInactive,
    setShouldDisplayCaption,
} from '../screen/screenActions';
import { pauseAudio, resumeAudio } from '../audio/audioActions';
import { ARIA_LABELS } from '../../constants/text';
import PauseButtonActive from '../../images/vector/pause-btn-ar.svg';
import PauseButtonInactive from '../../images/vector/pause-btn-inactive-ar.svg';
import ResumeButton from '../../images/vector/resume-btn-ar.svg';
import Alyx from '../components/Alyx';
import AvatarPosition from '../avatar/avatarConstants';
import { ApolloContext } from '../../ApolloContextProvider';

const AlexWrapper = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: flex-start;
    top: -1px;
`;

const StyledButton = styled.button`
    display: flex;
    flex: 0 1 auto;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 221px;
    height: 112px;
    background-color: transparent;
    border: none;
    box-shadow: none;
    cursor: pointer;
    z-index: 3;

    &:active {
        opacity: 0.9;
    }

    &:focus-visible {
        border: 3px solid #100163;
        outline: 4px solid #fff;
        border-top: 0px;
    }

    padding: 0px;
`;

/**
 * This is Alex! She sits at the top of the game header and talks to you about
 * the game you're playing.
 */
const Alex = ({ avatarConfig }) => {
    const { isAnimationVisualReduced } = useContext(ApolloContext);
    const dispatch = useDispatch();
    const alexState = useSelector(selectors.getAlexState);
    const isAlexAnimationDisabled = useSelector(selectors.getIsAnimationDisabled);
    const avatarInstructions = useSelector(selectors.getAvatarInstructions);
    const isAvatarSpeaking = useSelector(selectors.getAvatarSpeaking);
    const avatarOnTransitionEnd = useSelector(selectors.getAvatarOnTransitionEnd);
    const isPauseButtonActive = useSelector(selectors.getIsPauseButtonActive);
    const isPaused = useSelector(selectors.getIsPaused);
    const isScreenBlocked = useSelector(selectors.getIsScreenBlocked);
    const isConfirmPopupActive = useSelector(selectors.isConfirmPopupActive);
    const isHelpPopupVisible = useSelector(selectors.getIsHelpPopupVisible);
    const caption = useSelector(selectors.getCaption);
    const { animateAlex } = avatarConfig;

    const hasNoInstructions =
        avatarInstructions === undefined ||
        (Array.isArray(avatarInstructions) && !avatarInstructions.length);

    useEffect(() => {
        if (isAnimationVisualReduced) {
            dispatch(setAlexState(ALEX_STATES.INACTIVE));
            return;
        }
        if (isAvatarSpeaking && !isAlexAnimationDisabled) {
            if (alexState !== ALEX_STATES.SPEAKING) {
                dispatch(setAlexState(ALEX_STATES.SPEAKING));
            }
            if (!isPauseButtonActive) {
                dispatch(setPauseButtonActive());
            }
        }

        if (!isAvatarSpeaking) {
            if (alexState === ALEX_STATES.SPEAKING) {
                dispatch(setAlexState(ALEX_STATES.INACTIVE));
            }
            if (isPauseButtonActive) {
                dispatch(setPauseButtonInactive());
            }
        }
    }, [isAvatarSpeaking, isAlexAnimationDisabled, isAnimationVisualReduced]);

    useEffect(() => {
        if (typeof avatarOnTransitionEnd === 'function') {
            avatarOnTransitionEnd();
        }
    }, [avatarOnTransitionEnd]);

    useEffect(() => {
        dispatch(setReadyToAnimate());
    }, []);

    const onClick = () => {
        if (isAnimationVisualReduced) {
            const isAnimationOFF = true;
            dispatch(setAlexState(ALEX_STATES.INACTIVE));
            dispatch(playAlexAudio(avatarInstructions, caption, isAnimationOFF));
            return;
        }
        if (hasNoInstructions) return;
        if (isAvatarSpeaking || alexState === ALEX_STATES.SPEAKING) return;
        dispatch(playAlexAudio(avatarInstructions, caption));
    };

    const onPause = () => {
        dispatch(stopSpeaking());
        if (alexState === ALEX_STATES.SPEAKING) {
            dispatch(setAlexState(ALEX_STATES.INACTIVE));
        }
        dispatch(pauseAudio());
        dispatch(setShouldDisplayCaption(true));
    };

    const onResume = () => {
        dispatch(startSpeaking());
        if (!isAlexAnimationDisabled) {
            dispatch(setAlexState(ALEX_STATES.SPEAKING));
        }
        dispatch(resumeAudio());
    };

    const getIsAlexDisabled = () =>
        isScreenBlocked ||
        isAlexAnimationDisabled ||
        isHelpPopupVisible ||
        isConfirmPopupActive ||
        hasNoInstructions;

    return (
        <AlexWrapper aria-hidden={isHelpPopupVisible}>
            <StyledButton
                aria-label={ARIA_LABELS.ASK_ALYX_AGAIN}
                data-testid={`${alexState}-Alex`}
                disabled={getIsAlexDisabled()}
                onClick={onClick}
                type="button"
            >
                {animateAlex ? <AlexLottie currentState={alexState} /> : <Alyx />}
            </StyledButton>
            {isPaused ? (
                <div className="pause-overlay">
                    <div className="pause-banner">
                        <button
                            aria-label={ARIA_LABELS.CONTINUE_DIRECTIONS}
                            className="resume"
                            onClick={onResume}
                            type="button"
                        >
                            <ResumeButton className="icon-play" />
                        </button>
                        <span>Paused</span>
                    </div>
                </div>
            ) : (
                <button
                    aria-label={ARIA_LABELS.PAUSE_DIRECTIONS}
                    className="pause"
                    disabled={!isPauseButtonActive}
                    onClick={onPause}
                    type="button"
                >
                    {isPauseButtonActive ? (
                        <PauseButtonActive className="icon-pause" />
                    ) : (
                        <PauseButtonInactive className="icon-pause-inactive" />
                    )}
                </button>
            )}
        </AlexWrapper>
    );
};

export default Alex;

Alex.propTypes = {
    avatarConfig: PropTypes.shape({
        avatarPosition: PropTypes.oneOf(Object.values(AvatarPosition)).isRequired,
        animateAlex: PropTypes.bool.isRequired,
    }).isRequired,
};
